import React, {useState} from 'react';
import {Button} from '@mui/material';
import {useLazyQuery, useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {UPDATE_CONTACT, GET_ACCOUNT_BASIC} from './accountOverviewQueries';
import {UpdateContact, UpdateContactVariables} from '../../schema-types/UpdateContact';
import {
  AccountFull_accountDetails,
  AccountFull_accountDetails_contacts,
} from '../../schema-types/AccountFull';
import {
  SingleAccountBasic,
  SingleAccountBasicVariables,
} from '../../schema-types/SingleAccountBasic';
import {
  GetNativeIdFromCuro,
  GetNativeIdFromCuroVariables,
} from '../../schema-types/GetNativeIdFromCuro';
import {GET_NATIVE_ID_FROM_CURO} from '../../pages/TestSetupPages/TestHomepage';
import {OneViewModelTypesEnum} from '../../schema-types/globalTypes';
import validateUUID from '../../utils/validateUUID';

interface RegressionAccount {
  curoId: string;
  primaryName: string;
  type: string;
}

interface AddAccountDummyDataButtonProps {
  account?: AccountFull_accountDetails | null;
}

interface AddBulkAccountDummyDataButtonProps {
  accounts: Record<string, RegressionAccount>;
}

const updateContactsWithDummyData = async (
  contacts: AccountFull_accountDetails_contacts[],
  updateContact: (options: {variables: UpdateContactVariables}) => Promise<unknown>
) => {
  await Promise.all(
    contacts.map((contact) => {
      const fakeNI = `AB${Math.floor(Math.random() * 900000 + 100000)}C`;

      const variables: UpdateContactVariables = {
        contactId: contact.id,
        nationalInsurance: fakeNI,
        nationality: 'British',
        country: 'United Kingdom',
      };

      if (!contact.dob) {
        variables.dob = new Date('1970-01-01');
      } else {
        const dobDate = new Date(contact.dob);
        const invalidDob = new Date('1900-01-01');
        if (dobDate.getTime() === invalidDob.getTime()) {
          variables.dob = new Date('1970-01-01');
        }
      }

      return updateContact({variables});
    })
  );
};

export const AddBulkAccountDummyDataButton: React.FC<
  AddBulkAccountDummyDataButtonProps
> = ({accounts}) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [isProcessing, setIsProcessing] = useState(false);
  const [getNativeId] = useLazyQuery<GetNativeIdFromCuro, GetNativeIdFromCuroVariables>(
    GET_NATIVE_ID_FROM_CURO
  );
  const [getAccountBasic] = useLazyQuery<SingleAccountBasic, SingleAccountBasicVariables>(
    GET_ACCOUNT_BASIC
  );
  const [updateContact] = useMutation<UpdateContact, UpdateContactVariables>(
    UPDATE_CONTACT
  );

  const handleAddBulkDummyData = async () => {
    const accountList = Object.values(accounts);

    if (accountList.length === 0) {
      enqueueSnackbar('No accounts found', {variant: 'warning'});
      return;
    }

    setIsProcessing(true);
    const loadingKey = enqueueSnackbar(
      `Adding dummy data to ${accountList.length} accounts...`,
      {
        persist: true,
        variant: 'info',
      }
    );

    let successCount = 0;
    let errorCount = 0;

    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const account of accountList) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const {data: nativeIdData} = await getNativeId({
            variables: {
              curoId: validateUUID(account.curoId)!,
              modelName: OneViewModelTypesEnum.ACCOUNT,
            },
          });

          const nativeAccountId = nativeIdData?.nativeId;

          if (!nativeAccountId) {
            console.error(`Failed to get native ID for account ${account.primaryName}`);
            errorCount += 1;
            // eslint-disable-next-line no-continue
            continue;
          }

          // eslint-disable-next-line no-await-in-loop
          const {data} = await getAccountBasic({
            variables: {
              accountId: validateUUID(nativeAccountId)!,
              curoSync: true,
            },
          });

          const contacts = data?.accountDetails?.contacts || [];

          if (contacts.length > 0) {
            // eslint-disable-next-line no-await-in-loop
            await updateContactsWithDummyData(contacts, updateContact);
            successCount += 1;
          }
        } catch (error) {
          console.error(`Failed to update account ${account.primaryName}:`, error);
          errorCount += 1;
        }
      }

      closeSnackbar(loadingKey);

      if (errorCount === 0) {
        enqueueSnackbar(`Successfully added dummy data to all ${successCount} accounts`, {
          variant: 'success',
        });
      } else {
        enqueueSnackbar(
          `Completed with ${successCount} successes and ${errorCount} errors`,
          {
            variant: 'warning',
          }
        );
      }
    } catch (error) {
      closeSnackbar(loadingKey);
      enqueueSnackbar('Failed to add bulk dummy data', {variant: 'error'});
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Button
      size="small"
      variant="contained"
      color="primary"
      onClick={handleAddBulkDummyData}
      disabled={isProcessing}
    >
      Add Dummy Data to All Regression Accounts {isProcessing ? '(Processing...)' : ''}
    </Button>
  );
};

export const AddAccountDummyDataButton: React.FC<AddAccountDummyDataButtonProps> = ({
  account,
}) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [updateContact] = useMutation<UpdateContact, UpdateContactVariables>(
    UPDATE_CONTACT
  );

  const handleAddDummyData = async () => {
    if (!account?.contacts || account.contacts.length === 0) {
      enqueueSnackbar('No contacts found on this account', {variant: 'warning'});
      return;
    }

    const loadingKey = enqueueSnackbar('Adding dummy data to all contacts...', {
      persist: true,
      variant: 'info',
    });

    try {
      await updateContactsWithDummyData(account.contacts, updateContact);
      closeSnackbar(loadingKey);
      enqueueSnackbar(
        `Successfully added dummy data to ${account.contacts.length} contact(s)`,
        {variant: 'success'}
      );
    } catch (error) {
      closeSnackbar(loadingKey);
      enqueueSnackbar('Failed to add dummy data', {variant: 'error'});
      console.error(error);
    }
  };

  return (
    <Button size="small" variant="contained" color="primary" onClick={handleAddDummyData}>
      Add account dummy data
    </Button>
  );
};
